import { useState } from "react";
import { getMailsByRole, getNpcsByRole } from "../../../mocks/inbox";
import { useRole } from "../../../contexts/RoleContext";

interface Props {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const statusConfig: Record<string, { label: string; cls: string }> = {
  urgent: { label: "긴급", cls: "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" },
  ongoing: { label: "진행중", cls: "text-[#888888] bg-white/[0.04] border-white/[0.07]" },
  waiting: { label: "회신대기", cls: "text-[#666666] bg-white/[0.03] border-white/[0.05]" },
  "action-needed": { label: "조치필요", cls: "text-[#AAAAAA] bg-white/[0.05] border-white/[0.08]" },
};

const filterOptions = ["전체", "미읽음", "긴급", "진행중"];

export default function MailList({ selectedId, onSelect }: Props) {
  const { jobRole } = useRole();
  const [filter, setFilter] = useState("전체");
  const [search, setSearch] = useState("");

  const allMails = getMailsByRole(jobRole);
  const npcs = getNpcsByRole(jobRole);

  const getNpc = (id: string) => npcs.find((c) => c.id === id);

  const filtered = allMails.filter((m) => {
    if (filter === "미읽음" && m.isRead) return false;
    if (filter === "긴급" && m.status !== "urgent") return false;
    if (filter === "진행중" && m.status !== "ongoing") return false;
    const npc = getNpc(m.npcId);
    if (search && !m.subject.includes(search) && !npc?.name.includes(search)) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-3 py-2.5 border-b border-white/[0.04]">
        <div className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-[#666666] text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="메일 검색..."
            className="w-full bg-white/[0.03] text-[#CCCCCC] text-xs rounded-lg pl-7 pr-3 py-2 outline-none placeholder-[#555555] border border-white/[0.05] focus:border-[#4B6CF7]/30"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5 px-3 py-2 border-b border-white/[0.04] flex-wrap">
        {filterOptions.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`text-[9px] px-2 py-0.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
              filter === f
                ? "border-[#4B6CF7]/40 text-[#7A9BFF] bg-[#4B6CF7]/10"
                : "border-white/[0.06] text-[#444444] hover:border-[#4B6CF7]/30 hover:text-[#7A9BFF]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Mail items */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <i className="ri-mail-open-line text-2xl text-[#444444] mb-2"></i>
            <p className="text-[#666666] text-xs">메일이 없습니다</p>
          </div>
        ) : (
          filtered.map((mail) => {
            const npc = getNpc(mail.npcId);
            const isSelected = selectedId === mail.id;
            const cfg = statusConfig[mail.status];
            return (
              <div
                key={mail.id}
                onClick={() => onSelect(mail.id)}
                className={`px-3 py-3 border-b border-white/[0.03] cursor-pointer transition-all ${
                  isSelected
                    ? "bg-[#4B6CF7]/[0.06] border-l-2 border-l-[#4B6CF7]"
                    : "hover:bg-white/[0.02]"
                } ${!mail.isRead ? "bg-[#141416]" : ""}`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] text-[#777777] text-xs font-semibold shrink-0">
                    {npc?.avatar ?? "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span
                        className={`text-xs font-medium truncate ${
                          !mail.isRead ? "text-[#CCCCCC]" : "text-[#888888]"
                        }`}
                      >
                        {npc?.name}
                      </span>
                      <span className="text-[#666666] text-[9px] shrink-0 ml-1">
                        {mail.date.split(" ")[1]}
                      </span>
                    </div>
                    <p
                      className={`text-[11px] truncate ${
                        !mail.isRead ? "text-[#888888]" : "text-[#666666]"
                      }`}
                    >
                      {mail.subject}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[#666666] text-[9px] truncate">{npc?.company}</span>
                      {cfg && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${cfg.cls}`}>
                          {cfg.label}
                        </span>
                      )}
                    </div>
                  </div>
                  {!mail.isRead && (
                    <div className="w-1.5 h-1.5 bg-[#4B6CF7] rounded-full shrink-0 mt-1.5"></div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
